import { useCallback, useState } from 'react';

import {
    MAPBOX_GEOCODING_HOST,
    MAPBOX_REVERSE_GEOCODE_PATH,
    PREFERRED_FEATURE_TYPES,
    REGION_COUNTRY_SUFFIX,
    REGION_FEATURE_TYPES
} from './constants';
import type {
    FetchLocation,
    MapboxGeocodingResponse,
    ReverseGeocodeResult,
    UseReverseGeocodeReturn
} from './types';

const pickBestFeatureName = (
    response: MapboxGeocodingResponse,
    preferredTypes: string[]
): string | null => {
    const features = response.features ?? [];

    for (const type of preferredTypes) {
        const match = features.find(feature => feature.place_type?.includes(type));

        if (match?.place_name) {
            return match.place_name;
        }
    }

    return null;
};

const stripTrailingCountry = (placeName: string): string => {
    if (placeName.endsWith(REGION_COUNTRY_SUFFIX)) {
        return placeName.slice(0, -REGION_COUNTRY_SUFFIX.length);
    }

    return placeName;
};

export const parseReverseGeocode = (response: MapboxGeocodingResponse): ReverseGeocodeResult => {
    const address =
        pickBestFeatureName(response, PREFERRED_FEATURE_TYPES) ??
        response.features?.[0]?.place_name ??
        null;

    const regionRaw = pickBestFeatureName(response, REGION_FEATURE_TYPES);
    const region = regionRaw === null ? null : stripTrailingCountry(regionRaw);

    return { address, region };
};

const useReverseGeocode = (): UseReverseGeocodeReturn => {
    const [isLoading, setIsLoading] = useState(false);
    const [address, setAddress] = useState<string | null>(null);
    const [region, setRegion] = useState<string | null>(null);

    const fetchLocation: FetchLocation = useCallback(async ({ lat, lng }) => {
        const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string;

        if (!token) {
            return;
        }

        try {
            setIsLoading(true);

            const url = `${MAPBOX_GEOCODING_HOST}${MAPBOX_REVERSE_GEOCODE_PATH}/${lng},${lat}.json?access_token=${token}`;
            const response = await fetch(url);

            if (!response.ok) {
                setAddress(null);
                setRegion(null);

                return;
            }

            const body = (await response.json()) as MapboxGeocodingResponse;
            const resolved = parseReverseGeocode(body);

            setAddress(resolved.address);
            setRegion(resolved.region);
        } catch (error) {
            console.error('Reverse geocode failed', error);
            setAddress(null);
            setRegion(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    return { address, fetchLocation, isLoading, region };
};

export default useReverseGeocode;
